import { useEffect, useState } from "react";
import Cards from "./components/CardsSummary";
import { Box, Stack, Typography, Autocomplete, TextField, Divider } from "@mui/material";
import dayjs from "dayjs";
import { useResponsive } from "../../utils/mobileresponsive";
import FloatingSupportButton from "../../components/FloatingSupportButton";
import useFetchDashboard from "./hooks/useFetchDashboardData";
import useFetchAgentsOptions from "../../sharedhooks/Agents/useFetchAllAgents.js";
import DashboardSkeleton from "./components/DashboardSkeleton.jsx";
import { isFieldAgent } from "../../utils/roleDetection";

const getCurrentPeriod = () => {
	const now = new Date();
	const day = now.getDate();
	let year = now.getFullYear();
	let month = now.getMonth() + 1;

	if (day > 15) {
		month++;
		if (month > 12) {
			month = 1;
			year++;
		}
	}

	return `${year}-${String(month).padStart(2, "0")}`;
};

const Dashboard = () => {
	const [user, setUser] = useState(null);
	const [isAgent, setIsAgent] = useState(false);
	const { isMobile } = useResponsive();
	const { agents, loading: agentOptionsLoading } = useFetchAgentsOptions();

	const [selectedMonth, setSelectedMonth] = useState(() => {
		const period = getCurrentPeriod();
		const [year, month] = period.split("-");
		return dayjs(`${year}-${month}-01`);
	});

	useEffect(() => {
		const stored = localStorage.getItem("loggedin_data");

		if (stored) {
			const parsedUser = JSON.parse(stored);
			setUser(parsedUser);

			const roles = Array.isArray(parsedUser?.roles) ? parsedUser.roles : [];
			setIsAgent(isFieldAgent(roles));
		}
	}, []);

	const [currentAgent, setCurrentAgent] = useState(null);

	useEffect(() => {
		if (agents?.length > 0 && !currentAgent) {
			setCurrentAgent(agents[0]);
		}
	}, [agents, currentAgent]);

	const selectedAgentId = isAgent ? undefined : currentAgent?.ID;
	const selectedPeriod = selectedMonth.format("YYYY-MM");

	const { dashboard, loading } = useFetchDashboard(selectedAgentId, selectedPeriod);

	const milestonesAchieved = Array.isArray(dashboard?.milestone_achieved) ? dashboard.milestone_achieved : [];

	const overrideTotalNum = parseFloat(dashboard?.override_total) || 0;
	const leadershipThisMonth = Number(dashboard?.leadership_current_month_total) || 0;
	const networkResidueTotal = parseFloat(dashboard?.network_residue_total_income) || overrideTotalNum + leadershipThisMonth;

	const dashboardStats = dashboard
		? {
				qualification_status: dashboard.qualification_status,
				override_status: dashboard.override_status ? "active" : "inactive",
				is_activated: dashboard.is_activated !== false,
				has_period_data: dashboard.has_period_data !== false,
				period_label: selectedMonth.format("MMMM YYYY"),
				active_members: dashboard.active_members,
				monthly_service_revenue: dashboard.monthly_service_revenue,
				active_members_percent: dashboard.active_members_percent,
				network_members_percent: dashboard.network_members_percent,
				leadership_bonus_percent: dashboard.leadership_bonus_percent,

				l1_agents: dashboard.l1_agents,
				l2_agents: dashboard.l2_agents,
				l3_agents: dashboard.l3_agents,
				network_members: dashboard.total_network_members,

				override_l1: parseFloat(dashboard.override_l1) || 0,
				override_l2: parseFloat(dashboard.override_l2) || 0,
				override_l3: parseFloat(dashboard.override_l3) || 0,
				override_total: overrideTotalNum,

				override_percentage: dashboard.override_percentage,

				leadership_bonus: dashboard.leadership_bonus,
				leadership_bonus_earned_this_month: leadershipThisMonth,
				milestone_achieved: milestonesAchieved,

				network_residue_total_income: networkResidueTotal,

				total_agent_checkout: dashboard.total_agent_checkout,
			}
		: {};

	return (
		<Box p={4}>
			{!isAgent && (
				<>
					<Stack direction={{ xs: "column", lg: "row" }} alignItems="center !important" justifyContent={"space-between"} spacing={2} px={3}>
						<Typography variant={isMobile ? "h2" : "h4"} sx={{ fontWeight: "bold !important", color: "#1D54A2", letterSpacing: 4 }}>
							ADMIN DASHBOARD
						</Typography>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							alignItems={{ xs: "flex-start", sm: "center" }}
							spacing={{ xs: 0.75, sm: 2 }}
							width={{ xs: "100%", lg: "auto" }}
						>
							<Typography fontWeight={600} sx={{ fontSize: { xs: 13, sm: 14 }, whiteSpace: "nowrap" }}>
								Current Agent:
							</Typography>
							<Autocomplete
								size="small"
								options={agents || []}
								loading={agentOptionsLoading}
								value={currentAgent}
								onChange={(e, newValue) => setCurrentAgent(newValue)}
								getOptionLabel={(option) => option?.display_name || ""}
								isOptionEqualToValue={(option, value) => option?.ID === value?.ID}
								sx={{
									width: { xs: "100%", sm: 220, md: 250 },
									"& .MuiButtonBase-root": {
										backgroundColor: "transparent",
										"&:hover": { color: "#1D54A2" },
									},
								}}
								slotProps={{
									popper: {
										sx: {
											"& .MuiAutocomplete-paper": { width: "inherit !important" },
										},
									},
								}}
								renderInput={(params) => (
									<TextField
										{...params}
										placeholder="Select agent"
										sx={{
											"& .MuiInputBase-input": {
												padding: { xs: "6px 10px", sm: "8.5px 14px" },
												fontSize: { xs: 13, sm: 14 },
											},
											"& fieldset": { borderColor: "#4E4E4E !important" },
											"&:hover fieldset": { borderColor: "#4E4E4E !important" },
											"&.Mui-focused fieldset": { borderColor: "#4E4E4E !important" },
											"& .MuiAutocomplete-input": { border: "none !important" },
										}}
									/>
								)}
							/>
						</Stack>
					</Stack>
					<Box my={3}>
						<Divider />
					</Box>
				</>
			)}

			{loading || agentOptionsLoading ? (
				<DashboardSkeleton />
			) : (
				<Cards stats={dashboardStats} isAgent={isAgent} selectedMonth={selectedMonth} onOverridesMonthChange={setSelectedMonth} />
			)}
			{isAgent && <FloatingSupportButton />}
		</Box>
	);
};

export default Dashboard;
